// Redux Users module
// see https://github.com/erikras/ducks-modular-redux

import { User } from '../../models';
import Users from '../../services/rest/Users';
import { Utils } from '../../services';
import { updateAuthUser } from './auth';
import { isSpoofSessionActive, isSpoofActorSuperAdmin } from '../../services/spoofSession';
import i18n from '../../locales/i18n';
import { createRestSlices, applyReducers } from './rest';

const LOADING_USERS                  = 'kronos/users/LOADING_USERS';
const LOADING_USERS_SUCCESS          = 'kronos/users/LOADING_USERS_SUCCESS';
const LOADING_USERS_FAILURE          = 'kronos/users/LOADING_USERS_FAILURE';
const LOADING_INACTIVE_USERS         = 'kronos/users/LOADING_INACTIVE_USERS';
const LOADING_INACTIVE_USERS_SUCCESS = 'kronos/users/LOADING_INACTIVE_USERS_SUCCESS';
const LOADING_INACTIVE_USERS_FAILURE = 'kronos/users/LOADING_INACTIVE_USERS_FAILURE';
const LOADING_USER                   = 'kronos/users/LOADING_USER';
const LOADING_USER_SUCCESS           = 'kronos/users/LOADING_USER_SUCCESS';
const LOADING_USER_FAILURE           = 'kronos/users/LOADING_USER_FAILURE';
const UPDATING_USER                  = 'kronos/users/UPDATING_USER';
const UPDATING_USER_SUCCESS          = 'kronos/users/UPDATING_USER_SUCCESS';
const UPDATING_USER_FAILURE          = 'kronos/users/UPDATING_USER_FAILURE';
const START_CREATING_USER            = 'kronos/users/START_CREATING_USER';
const PREPARE_CREATE_USER            = 'kronos/users/PREPARE_CREATE_USER';
const CREATING_USER                  = 'kronos/users/CREATING_USER';
const CREATING_USER_SUCCESS          = 'kronos/users/CREATING_USER_SUCCESS';
const CREATING_USER_FAILURE          = 'kronos/users/CREATING_USER_FAILURE';
const DELETING_USER                  = 'kronos/users/DELETING_USER';
const DELETING_USER_SUCCESS          = 'kronos/users/DELETING_USER_SUCCESS';
const DELETING_USER_FAILURE          = 'kronos/users/DELETING_USER_FAILURE';
const APPLY_USER_ABILITIES_TEMPLATE  = 'kronos/users/APPLY_USER_ABILITIES_TEMPLATE';
const APPLY_BATCH_ABILITIES          = 'kronos/users/APPLY_BATCH_ABILITIES';
const APPLY_BATCH_ABILITIES_SUCCESS  = 'kronos/users/APPLY_BATCH_ABILITIES_SUCCESS';
const APPLY_BATCH_ABILITIES_FAILURE  = 'kronos/users/APPLY_BATCH_ABILITIES_FAILURE';
const ADD_SECTOR_USER                = 'kronos/users/ADD_SECTOR_USER';
const ADD_SECTOR_USER_SUCCESS        = 'kronos/users/ADD_SECTOR_USER_SUCCESS';
const ADD_SECTOR_USER_FAILURE        = 'kronos/users/ADD_SECTOR_USER_FAILURE';
const REMOVE_SECTOR_USER             = 'kronos/users/REMOVE_SECTOR_USER';
const REMOVE_SECTOR_USER_SUCCESS     = 'kronos/users/REMOVE_SECTOR_USER_SUCCESS';
const REMOVE_SECTOR_USER_FAILURE     = 'kronos/users/REMOVE_SECTOR_USER_FAILURE';
const REMOVE_USER_AVATAR             = 'kronos/users/REMOVE_USER_AVATAR';

const APPROVING_APPLICATION_SUCCESS = 'kronos/sectors/APPROVING_APPLICATION_SUCCESS';
const REJECTING_APPLICATION_SUCCESS = 'kronos/sectors/REJECTING_APPLICATION_SUCCESS';

const {
    initialState,
    linkedResourcesReducer, linkedResourcesAction
} = createRestSlices(Users);

initialState.isLoading                  = false;
initialState.isCreatingUser             = false;
initialState.hasLoadingError            = false;
initialState.loadingErrorMessage        = '';
initialState.userSuccessfullyCreated    = false;
initialState.userSuccessfullyUpdated    = false;
initialState.user                       = null;
initialState.createLookupMessage        = '';
initialState.createSuccessMessage       = '';
initialState.createEmailWarning         = '';
initialState.createProvisionOutcome     = '';
initialState.templateApplied            = false;
initialState.applyBatchAbilitiesMessage = '';

// Reducer
const reducer = (state = initialState, action) => {
    switch(action.type) {
        case LOADING_USERS:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case LOADING_USERS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                userSuccessfullyDeleted: false,
                users: action.users
            };
        case LOADING_USERS_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case LOADING_INACTIVE_USERS:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case LOADING_INACTIVE_USERS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                inactive_users: action.users
            };
        case LOADING_INACTIVE_USERS_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case LOADING_USER:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case LOADING_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                userSuccessfullyCreated: false,
                user: action.user
            };
        case LOADING_USER_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case ADD_SECTOR_USER:
        case REMOVE_SECTOR_USER:
        case UPDATING_USER:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                userSuccessfullyUpdated: false,
            };
        case UPDATING_USER_SUCCESS:
        case ADD_SECTOR_USER_SUCCESS:
        case REMOVE_SECTOR_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                user: action.user,
                userSuccessfullyUpdated: true,
                templateApplied: false
            };
        case UPDATING_USER_FAILURE:
        case ADD_SECTOR_USER_FAILURE:
        case REMOVE_SECTOR_USER_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message,
                userSuccessfullyUpdated: false
            };
        case START_CREATING_USER:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                userSuccessfullyCreated: false,
                user: null,
                createSuccessMessage: '',
                createEmailWarning: '',
                createProvisionOutcome: '',
                isCreatingUser: false,
            };
        case PREPARE_CREATE_USER:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                userSuccessfullyCreated: false,
                user: action.prefill ? new User(action.prefill) : null,
                createLookupMessage: action.message || '',
            };
        case CREATING_USER:
            return {
                ...state,
                isLoading: true,
                isCreatingUser: true,
                hasLoadingError: false,
                loadingErrorMessage: '',
            };
        case CREATING_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isCreatingUser: false,
                hasLoadingError: false,
                userSuccessfullyCreated: true,
                user: action.user,
                createSuccessMessage: action.successMessage || '',
                createEmailWarning: action.emailWarning || '',
                createProvisionOutcome: action.provisionOutcome || '',
            };
        case CREATING_USER_FAILURE:
            return {
                ...state,
                isLoading: false,
                isCreatingUser: false,
                hasLoadingError: true,
                userSuccessfullyCreated: false,
                loadingErrorMessage: action.error.message
            };
        case DELETING_USER:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case DELETING_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                userSuccessfullyDeleted: true,
                user: null,
                users: action.userId
                    ? (state.users || []).filter((u) => u.id !== action.userId)
                    : state.users,
            };
        case DELETING_USER_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                userSuccessfullyDeleted: false,
                loadingErrorMessage: action.error.message
            };
        case APPLY_USER_ABILITIES_TEMPLATE:
            return {
                ...state,
                user: new User({
                    ...state.user,
                    abilities : action.abilities
                }),
                templateApplied: true
            };
        case APPLY_BATCH_ABILITIES:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                applyBatchAbilitiesMessage: ''
            };
        case APPLY_BATCH_ABILITIES_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                loadingErrorMessage: '',
                applyBatchAbilitiesMessage: action.message
            };
        case APPLY_BATCH_ABILITIES_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                userSuccessfullyCreated: false,
                loadingErrorMessage: action.error.message,
                applyBatchAbilitiesMessage: ''
            };
        case REMOVE_USER_AVATAR:
            return {
                ...state,
                user: new User({
                    ...state.user,
                    avatar : null
                }),
            };
        case APPROVING_APPLICATION_SUCCESS:
            if (!state.user)
                return state;
            return {
                ...state,
                user: new User({
                    ...state.user,
                    applications: (state.user.applications || []).map(a => {
                        if (a.id === action.application.id)
                            return action.application;
                        return a;
                    })
                })
            };
        case REJECTING_APPLICATION_SUCCESS:
            if (!state.user)
                return state;
            return {
                ...state,
                user: new User({
                    ...state.user,
                    applications: (state.user.applications || []).map(a => {
                        if (a.id === action.application.id)
                            return action.application;
                        return a;
                    })
                })
            };
        default:
            return state;
    }
};
export default (state = initialState, action) => {
    return applyReducers(state, action, [
        linkedResourcesReducer, reducer
    ]);
}

// Actions
function loadingUsers() { return { type: LOADING_USERS } }
function loadingUsersSuccess(users) { return { type: LOADING_USERS_SUCCESS, users: users } }
function loadingUsersFailure(err) { return { type: LOADING_USERS_FAILURE, error: err } }
export function loadUsers(params) {
    return (dispatch) => {
        dispatch(loadingUsers());
        Users.list(params)
            .then(data => {
                const users = data.users;
                dispatch(loadingUsersSuccess(users));
            })
            .catch(err => {
                dispatch(loadingUsersFailure(err))
            });
    }
}

function loadingInactiveUsers() { return { type: LOADING_INACTIVE_USERS } }
function loadingInactiveUsersSuccess(users) { return { type: LOADING_INACTIVE_USERS_SUCCESS, users: users } }
function loadingInactiveUsersFailure(err) { return { type: LOADING_INACTIVE_USERS_FAILURE, error: err } }
export function loadInactiveUsers() {
    return (dispatch) => {
        dispatch(loadingInactiveUsers());
        Users.inactive()
            .then(data => {
                const users = data.users;
                dispatch(loadingInactiveUsersSuccess(users));
            })
            .catch(err => {
                dispatch(loadingInactiveUsersFailure(err))
            });
    }
}

function loadingUser() { return { type: LOADING_USER } }
function loadingUserSuccess(user) { return { type: LOADING_USER_SUCCESS, user: user } }
function loadingUserFailure(err) { return { type: LOADING_USER_FAILURE, error: err } }
export function loadUser(userId) {
    return (dispatch, getState) => {
        dispatch(loadingUser());
        Users.get(userId)
            .then(data => {
                const user = data.user;
                const auth_user = getState().auth?.user;
                const enabled_modules = auth_user?.tkt_instance?.options?.modules
                    ? Utils.getCurrentModules(auth_user.tkt_instance.options.modules)
                    : null;
                if (user?.abilities && enabled_modules?.length) {
                    user.abilities = Utils.mergeAbilitiesWithSchema(user.abilities, enabled_modules);
                }
                dispatch(loadingUserSuccess(user));

            })
            .catch(err => {
                dispatch(loadingUserFailure(err))
            });
    }
}

function updatingUser() { return { type: UPDATING_USER } }
function updatingUserSuccess(user) { return { type: UPDATING_USER_SUCCESS, user: user } }
function updatingUserFailure(err) { return { type: UPDATING_USER_FAILURE, error: err } }
export function updateUser(user, meta = {}) {
    return (dispatch, getState) => {
        if (isSpoofSessionActive() && meta.switching_tkt_instance && !isSpoofActorSuperAdmin()) {
            return Promise.reject(new Error(i18n.t('users.spoof_instance_switch_forbidden')));
        }
        dispatch(updatingUser());
        const rest_options = meta.switching_tkt_instance
            ? { skip_readonly_edition_guard: true }
            : {};
        const auth_user = getState().auth?.user;
        const enabled_modules = auth_user?.tkt_instance?.options?.modules
            ? Utils.getCurrentModules(auth_user.tkt_instance.options.modules)
            : null;
        const payload = { ...user };
        if (payload.abilities && enabled_modules?.length) {
            payload.abilities = Utils.mergeAbilitiesWithSchema(payload.abilities, enabled_modules);
        }
        return Users.update(payload, rest_options)
            .then(data => {
                const user = data.user;
                dispatch(updatingUserSuccess(user));
                dispatch(updateAuthUser(user));
                return data;
            })
            .catch(err => {
                dispatch(updatingUserFailure(err));
                throw err;
            });
    };
}

export function applyUserAbilitiesTemplate(abilities) {
    return (dispatch, getState) => {
        const auth_user = getState().auth?.user;
        const enabled_modules = auth_user?.tkt_instance?.options?.modules
            ? Utils.getCurrentModules(auth_user.tkt_instance.options.modules)
            : null;
        const merged = enabled_modules?.length
            ? Utils.mergeAbilitiesWithSchema(abilities, enabled_modules)
            : Utils.mergeAbilitiesWithSchema(abilities);
        return dispatch({ type: APPLY_USER_ABILITIES_TEMPLATE, abilities: merged });
    }
}

function applyBatchAbilities() { return { type: APPLY_BATCH_ABILITIES } }
function applyBatchAbilitiesSuccess(users, message) { return { type: APPLY_BATCH_ABILITIES_SUCCESS, users, message } }
function applyBatchAbilitiesFailure(err) { return { type: APPLY_BATCH_ABILITIES_FAILURE, error: err } }
export function applyUsersAbilities(templateName, ids, includeSelf = false) {
    return (dispatch) => {
        dispatch(applyBatchAbilities());
        Users.applyAbilities(templateName, ids, includeSelf)
            .then(data => {
                const { users, flash } = data;
                dispatch(applyBatchAbilitiesSuccess(users, flash?.success));
            })
            .catch(err => {
                dispatch(applyBatchAbilitiesFailure(err))
            });
    }
}



function creatingUser() { return { type: CREATING_USER } }
function creatingUserSuccess(user, meta = {}) {
    return {
        type: CREATING_USER_SUCCESS,
        user,
        successMessage: meta.successMessage || '',
        emailWarning: meta.emailWarning || '',
        provisionOutcome: meta.provisionOutcome || '',
    };
}
function creatingUserFailure(err) { return { type: CREATING_USER_FAILURE, error: err } }
export function startCreateUser() { return { type: START_CREATING_USER } }
export function prepareCreateUser(prefill, message = '') {
    return { type: PREPARE_CREATE_USER, prefill, message };
}
const is_duplicate_membership_error = (message) => (
    /kuser_tkt_instances|duplicate key|already_member/i.test(message || '')
);

const recover_created_user = (email) => Users.lookupByEmail(email)
    .then((json) => {
        const lookup = json.lookup;
        if (!lookup?.found || !lookup?.has_membership_on_instance || !lookup?.id) {
            return null;
        }
        return new User({
            id: lookup.id,
            email: lookup.email,
            firstname: lookup.firstname,
            lastname: lookup.lastname,
            gender: lookup.gender,
            tkt_instance_id: lookup.tkt_instance_id,
        });
    })
    .catch(() => null);

export function createUser(user) {
    return (dispatch, getState) => {
        if (getState().users.isCreatingUser) {
            return Promise.resolve();
        }

        dispatch(creatingUser());
        const payload = { ...user };
        delete payload['new-password'];
        delete payload['new-password-confirmation'];

        const finish_create = (data) => {
            const created_user = data.user;
            const success_message = data.flash?.success
                ? (Array.isArray(data.flash.success) ? data.flash.success.join(', ') : data.flash.success)
                : '';
            const email_warning = data.flash?.warning
                ? (Array.isArray(data.flash.warning) ? data.flash.warning.join(', ') : data.flash.warning)
                : '';
            dispatch(creatingUserSuccess(created_user, {
                successMessage: success_message,
                emailWarning: email_warning,
                provisionOutcome: data.provision_outcome || 'created',
            }));
            dispatch(loadUsers());
        };

        return Users.create(payload)
            .then(finish_create)
            .catch((err) => {
                if (!is_duplicate_membership_error(err.message) || !payload.email) {
                    dispatch(creatingUserFailure(err));
                    return;
                }

                return recover_created_user(payload.email)
                    .then((recovered_user) => {
                        if (!recovered_user) {
                            dispatch(creatingUserFailure(err));
                            return;
                        }
                        finish_create({
                            user: recovered_user,
                            flash: {},
                            provision_outcome: 'linked',
                        });
                    });
            });
    };
}

function deletingUser() { return { type: DELETING_USER } }
function deletingUserSuccess(userId) { return { type: DELETING_USER_SUCCESS, userId } }
function deletingUserFailure(err) { return { type: DELETING_USER_FAILURE, error: err } }
export function deleteUser(userId) {
    return (dispatch) => {
        dispatch(deletingUser());
        Users.hide(userId)
            .then(() => {
                dispatch(deletingUserSuccess(userId));
                dispatch(loadUsers());
            })
            .catch(err => {
                dispatch(deletingUserFailure(err))
            });
    }
}

function addSector() { return { type: ADD_SECTOR_USER } }
function addSectorSuccess(user) { return { type: ADD_SECTOR_USER_SUCCESS, user } }
function addSectorFailure(err) { return { type: ADD_SECTOR_USER_FAILURE, error: err } }
export function addSectorUser(params) {
    return (dispatch) => {
        dispatch(addSector());
        Users.addSector(params)
            .then(data => {
                dispatch(addSectorSuccess(data.user));
            })
            .catch(err => {
                dispatch(addSectorFailure(err))
            });
    }
}

function removeSector() { return { type: ADD_SECTOR_USER } }
function removeSectorSuccess(user) { return { type: ADD_SECTOR_USER_SUCCESS, user } }
function removeSectorFailure(err) { return { type: ADD_SECTOR_USER_FAILURE, error: err } }
export function removeSectorUser(params) {
    return (dispatch) => {
        dispatch(removeSector());
        Users.removeSector(params)
            .then(data => {
                dispatch(removeSectorSuccess(data.user));
            })
            .catch(err => {
                dispatch(removeSectorFailure(err))
            });
    }
}

export function removeUserAvatar(_params) {
    return (dispatch) => dispatch({ type: REMOVE_USER_AVATAR });
}

export const getUsersLinkedResources = linkedResourcesAction;
